import { Injectable, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { formatDate } from '@angular/common';
import { StepperOption } from '../../../../../../ui-components/lib/stepper/stepper-options.interface';
import { dualLanguageCustomValidators } from '../../../../../../form-builder/services/form/custom-validators';
import {
  Field,
  RequestFormGroup,
} from '../../../../../../form-builder/interface/request-form-builder.model';
import { inputTypesEnum } from '../../../../../../form-builder/enums/input-type';
import { FeatureFlagsService } from '../../../../../common/services/feature-flags/feature-flags.service';
import { StepStatusEnum } from '../../../../../common/enums/step-status';
import {
  RequestDetailsResponse,
  RequestPosition,
} from '../../../../../common/models/request-details.model';
import { RequestService } from '../../../../../common/services/request/request.service';

@Injectable({
  providedIn: 'root',
})
export class RequestComponentService {
  fb = inject(FormBuilder);
  private readonly requestService = inject(RequestService);
  inputTypesEnum = inputTypesEnum;
  isSubmitted = signal<boolean>(false);

  featureFlags = inject(FeatureFlagsService);

  drawStepper(requestDetailsResponse: RequestDetailsResponse) {
    const stepperOption: StepperOption[] = this.drawStepperByPosition(
      requestDetailsResponse.data.positions,
      requestDetailsResponse
    );
    return stepperOption;
  }

  drawStepperByPosition(
    positions: RequestPosition[],
    requestDetailsResponse?: RequestDetailsResponse
  ) {
    const stepperOption: StepperOption[] = [];
    positions?.forEach((p, index) => {
      const state = requestDetailsResponse
        ? this.setPositionStatus(index + 1, requestDetailsResponse)
        : this.setPositionStatusByPositionData(p);
      stepperOption.push({
        label: p.position_name,
        status: state as 'active' | 'done' | 'inactive',
        position: p.position,
        isParallel: p.steps.length > 1,
        parallelSteps: p.steps.map(s => {
          const mappedArray = s.details?.assignees?.map(assignee => {
            return {
              id: assignee.data.id,
              name: assignee.data.name,
              image: assignee?.data?.image ?? null,
              type: assignee.type,
              state: assignee.data?.state ?? null,
              assigneeRole: assignee.assignee_role ?? null,
            };
          });
          const rawValue = s.details?.state?.value || StepStatusEnum.NO_ACTION;
          const step_state_color = this.setStepStatusColor(rawValue);
          const badgeLabel = s.details?.state?.label
            ? s.details?.state?.label
            : `step_status_${rawValue}`;
          return {
            label: s.name,
            badge: badgeLabel,
            badge_class: step_state_color,
            isParallel: false,
            descriptionIconArray: mappedArray,
          };
        }),
        descriptionTemplateData: p,
      });
    });
    return stepperOption;
  }

  setPositionStatusByPositionData(position: RequestPosition): string {
    const currentPositionState = this.requestService.getPositionState(position);

    switch (currentPositionState?.value) {
      case StepStatusEnum.COMPLETED:
      case StepStatusEnum.REJECTED:
      case StepStatusEnum.SENT_BACK:
      case StepStatusEnum.SENT_FORWARD:
      case StepStatusEnum.SKIPPED:
        return 'done';
      case StepStatusEnum.INPROGRESS:
      case StepStatusEnum.ON_HOLD:
      case StepStatusEnum.PENDING:
        return 'active';
      default:
        return 'inactive';
    }
  }

  setPositionStatus(
    index: number,
    requestDetailsResponse: RequestDetailsResponse
  ): string {
    const current_position = requestDetailsResponse.data.current_position;
    if (requestDetailsResponse.data.state?.value === StepStatusEnum.COMPLETED) {
      return 'done';
    }
    if (current_position !== undefined) {
      if (current_position === index) {
        return 'active';
      } else if (current_position > index) {
        return 'done';
      } else if (current_position < index) {
        return 'inactive';
      }
    }
    return '';
  }

  setStepStatusColor(value: string | undefined | null): string {
    switch (value) {
      case StepStatusEnum.COMPLETED:
        return 'text-success-700 bg-success-50';

      case StepStatusEnum.INPROGRESS:
        return 'text-warning-700 bg-warning-50';

      case StepStatusEnum.REJECTED:
      case StepStatusEnum.SENT_BACK:
      case StepStatusEnum.SENT_FORWARD:
        return 'text-destructive-700 bg-destructive-50';

      case StepStatusEnum.SKIPPED:
        return '';

      case StepStatusEnum.PENDING:
        return 'text-warning-700 bg-warning-50';

      case StepStatusEnum.NOT_STARTED:
      case StepStatusEnum.ON_HOLD:
        return 'text-neutral-700 bg-neutral-100';
      default:
        return 'text-neutral-700 bg-neutral-50';
    }
  }

  buildRequestForm(requestFormGroups: RequestFormGroup[]) {
    const requestForm = new FormGroup({});

    requestFormGroups.forEach(groupItem => {
      let i = 0;
      const fields = groupItem.fields;

      while (i < fields.length) {
        const formItem = fields[i];

        if (
          formItem.type === inputTypesEnum.GROUP_INPUT_FIELD ||
          formItem.type === inputTypesEnum.TABLE_INPUT_FIELD ||
          formItem.type === inputTypesEnum.COMPOSITE_FIELD
        ) {
          // Create form array for group input field
          const formArray = this.fb.array<FormGroup>([]);

          if (formItem.is_required) {
            formArray.setValidators([Validators.required]);
          }
          requestForm.addControl(formItem.key, formArray);
          i++;
        } else {
          // Handle regular fields
          this.addFieldToFormGroup(requestForm, formItem);
          i++;
        }
      }
    });

    return requestForm;
  }

  private getFieldValidators(field: Field) {
    if (field.is_required && field.type === inputTypesEnum.SINGLE_CHECK_BOX) {
      return Validators.requiredTrue;
    } else if (field.is_required) {
      return Validators.required;
    } else {
      return null;
    }
  }

  // Helper method to handle dual language fields
  private addFieldToFormGroup(formGroup: FormGroup, field: Field): void {
    if (
      field.type === inputTypesEnum.DUAL_LANGUAGE_TEXT_INPUT_FIELD ||
      field.type === inputTypesEnum.DUAL_LANGUAGE_TEXT_AREA_FIELD ||
      field.type === inputTypesEnum.RICH_EDITOR_DUAL_LANGUAGE_TEXT_AREA_FIELD
    ) {
      // Handle dual language fields
      const initialValue = field.value ?? { en: null, ar: null };
      const validators = null;

      const enControl = this.fb.control(
        {
          value: initialValue.en ?? null,
          disabled: !field.can_edit,
        },
        validators
      );

      const arControl = this.fb.control(
        {
          value: initialValue.ar ?? null,
          disabled: !field.can_edit,
        },
        validators
      );

      formGroup.addControl(
        field.key,
        this.fb.group(
          {
            en: enControl,
            ar: arControl,
          },
          {
            validators: field.is_required
              ? [dualLanguageCustomValidators.atLeastOneLangRequired]
              : [],
          }
        )
      );
    } else {
      // Handle regular fields
      const validators = this.getFieldValidators(field);
      formGroup.addControl(
        field.key,
        this.fb.control(
          {
            value: this.setRequestFormItemValue(field),
            disabled: !field.can_edit,
          },
          validators
        )
      );
    }
  }

  setRequestFormItemValue(formItem: Field) {
    if (formItem.value && formItem.type === inputTypesEnum.FILE) {
      return formItem.value.name;
    } else if (formItem.value && formItem.type === inputTypesEnum.DATE_PICKER) {
      return formatDate(new Date(formItem.value), 'dd-MMM-yyyy', 'en');
    } else {
      return formItem.value;
    }
  }

  // handle prepare sent form data for request submission
  prepareRequestFormData(
    formValue: Record<string, unknown>,
    requestFormGroups: RequestFormGroup[]
  ): Record<string, unknown> {
    const formData = formValue;

    requestFormGroups.forEach(groupItem => {
      groupItem.fields.forEach(field => {
        this.prepareFieldDataByType(field, formValue, formData);
      });
    });

    return formData;
  }

  private prepareFieldDataByType(
    field: Field,
    formValue: Record<string, unknown>,
    formData: Record<string, unknown>
  ): void {
    if (field.type === inputTypesEnum.LOOKUP) {
      this.prepareLookupFieldData(field, formValue, formData);
      return;
    }

    if (this.isDualLanguageField(field)) {
      this.prepareDualLanguageFieldData(field, formValue, formData);
      return;
    }

    formData[field.key] = formValue[field.key];
  }

  private isDualLanguageField(field: Field): boolean {
    return (
      field.type === inputTypesEnum.DUAL_LANGUAGE_TEXT_INPUT_FIELD ||
      field.type === inputTypesEnum.DUAL_LANGUAGE_TEXT_AREA_FIELD ||
      field.type === inputTypesEnum.RICH_EDITOR_DUAL_LANGUAGE_TEXT_AREA_FIELD
    );
  }

  private prepareDualLanguageFieldData(
    field: Field,
    formValue: Record<string, unknown>,
    formData: Record<string, unknown>
  ): void {
    const value = formValue[field.key];

    if (field.multiple && value && !Array.isArray(value)) {
      formData[field.key] = [value];
      return;
    }

    formData[field.key] = value;
  }

  private prepareLookupFieldData(
    field: Field,
    formValue: Record<string, unknown>,
    formData: Record<string, unknown>
  ): void {
    const lookupValue = formValue[field.key] as Array<unknown>;

    if (
      field.multiple &&
      !field.is_required &&
      Array.isArray(lookupValue) &&
      lookupValue.some(item => !item)
    ) {
      (formData[field.key] as Array<number | null>).pop();
      if (!(formData[field.key] as Array<number | null>).length) {
        formData[field.key] = [];
      }
      return;
    }

    formData[field.key] = formValue[field.key];
  }
}
